import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Callout from "components/callout/callout";
import FormErrors from "components/formParts/formErrors";
import { useTranslation } from "react-i18next";
import TextInput from "components/inputs/textInput";
import useTransactions from "hooks/useTransactions";
import useLayout from "hooks/useLayout";
import Form from "components/forms/form";
import { ReactComponent as DeleteIcon } from "assets/icons/delete.svg";
import { ReactComponent as CloseIcon } from "assets/icons/close.svg";
import Button from "components/inputs/button";
import ButtonGroup from "components/buttons/buttonGroup";
import Section from "components/layout/section";
import ProcessingButton from "components/inputs/processingButton";
import { decodeFirebaseError } from "utils/firebaseHelpers";

interface DeleteTransactionFormProps {
    storageId: string;
    transactionId: string;
}

const DeleteTransactionForm: React.FC<DeleteTransactionFormProps> = ({ storageId, transactionId }) => {
    const { t } = useTranslation();

    const [errors, setErrors] = useState<string[]>([]);
    const [confirmation, setConfirmation] = useState("");
    const [confirmed, setConfirmed] = useState(false);
    const [transactionIdString, setTransactionIdString] = useState("");

    const navigate = useNavigate();

    const {
        transaction,
        transactionStatus,
        deleteTransactionAsync,
        isDeleting,
    } = useTransactions({ storageId, transactionId });

    const { closeDialog, addToast } = useLayout();

    // Catch transaction id from storage
    useEffect(() => {
        if (!transaction) return;
        if (transaction.transactionId !== undefined && transaction.transactionId !== null) {
            setTransactionIdString(transaction.transactionId.toString());
        }
    }, [transaction]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setErrors([]);
        if (!confirmed) {
            setErrors([t("errorConfirmAction")]);
            return;
        }
        try {
            await deleteTransactionAsync({ storageId, transactionId });
            addToast({ text: t("toastTransactionDeleted"), colorScheme: "green" });
            closeDialog();
            navigate(`/storage/transactions`);
        } catch (e: unknown) {
            const errorMessage = decodeFirebaseError(e instanceof Error ? e.message : String(e));
            setErrors([errorMessage]);
        }
    };

    const handleConfirmationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setConfirmation(e.target.value);
        setConfirmed(e.target.value === transactionIdString && transactionIdString.length > 0);
    };

    if (!storageId) return <Callout type={"error"}>{t("storageIdMissing")}</Callout>;
    if (!transactionId) return <Callout type={"error"}>{t("transactionIdMissing")}</Callout>;
    if (transactionStatus === "loading") return <Callout type={"neutral"} loadingIcon={true}>{t("loadingStorage")}</Callout>;

    return (
        <Form onSubmit={handleSubmit} className={"group"} isLoading={isDeleting}>
            <Section>
                <p>{t("deleteTransactionConfirmation", { id: transaction?.transactionId })}</p>
                <TextInput
                    label={t("typeTransactionIdToConfirm")}
                    value={confirmation}
                    onChange={handleConfirmationChange}
                    required
                    placeholder={transactionIdString}
                />
                <ButtonGroup>
                    <Button wideOnMobile type="button" design="optional" onClick={closeDialog}>
                        <CloseIcon />
                        <span>{t("buttonCancel")}</span>
                    </Button>
                    {isDeleting ? (
                        <ProcessingButton design="danger" />
                    ) : (
                        <Button wideOnMobile type="submit" design="danger">
                            <DeleteIcon />
                            <span>{t("delete")}</span>
                        </Button>
                    )}
                </ButtonGroup>
                {errors.length > 0 && <FormErrors errors={errors} />}
            </Section>
        </Form>
    );
};

export default DeleteTransactionForm;
